import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { filter, map, take } from 'rxjs/operators';

import { AuthService } from '@app/core/services/auth.service';
import { ClientRole } from '@app/core/models';

/**
 * CW-13d KycRequiredGuard (spec §5).
 *
 * Blocks navigation to any protected route until KYC is complete. Always
 * lets `/kyc` itself through so the user can reach the verification page.
 * Defers to `authGuard` for the unauthenticated case — when no user is
 * loaded, this guard passes through (`true`) so `authGuard` (mounted as
 * an earlier canActivate) drives the login redirect.
 *
 * `AuthService.isKycComplete()` already handles the user-type branching
 * (sub-user → `subUserState === 'verified'`, user → `finalKycStatus === true`),
 * including AD-020 precedence (rejected > verified > pending) which is
 * resolved server-side and returned in `subUserState`.
 *
 * Page-refresh race: when `restoreInProgress()` is true we await its
 * completion before deciding, matching authGuard's pattern.
 */
export const kycRequiredGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const evaluate = (): boolean | UrlTree => {
    const user = authService.getUser();

    // No user loaded: defer to authGuard (mounted upstream) to drive
    // the redirect-to-login flow. Returning `true` here means the layer
    // above this guard already cleared the access decision (or will).
    if (!user) return true;

    // Always allow the verification page itself — the user MUST be able
    // to reach it. Logout is also whitelisted so a blocked user can sign
    // out without first completing verification. Corporate tree mirrors
    // the same path under /corporate/kyc.
    if (
      state.url.startsWith('/kyc') ||
      state.url.startsWith('/corporate/kyc') ||
      state.url === '/auth/logout'
    ) {
      return true;
    }

    if (authService.isKycComplete()) return true;

    // Route corporate clients/sub-users to the corporate KYC page so they
    // stay inside the corporate layout shell; personal clients go to /kyc.
    // (The layout picks the shell from the URL prefix — see ClientLayout.)
    const role = authService.getUserRole();
    const isCorporate =
      role === ClientRole.CORPORATE_OWNER || role === ClientRole.CORPORATE_USER;
    return router.createUrlTree([isCorporate ? '/corporate/kyc' : '/kyc'], {
      queryParams: { returnUrl: state.url },
    });
  };

  if (authService.restoreInProgress()) {
    return toObservable(authService.restoreInProgress).pipe(
      filter((inProgress) => !inProgress),
      take(1),
      map(() => evaluate()),
    );
  }

  return evaluate();
};
